import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { VetDetailsList } from './vetDetailsList/vetDetailsList';
import { VetDetailPopup } from './vetDetailPopup/vetDetailPopup';
import { GetPetDetailsFromPolicyNumber, GetPetPolicyNumberDropdownOptions } from '../../helpers/petHelpers';
import { GetVetsByServiceProviderNumber } from '../../helpers/vetHelpers';
import { FullPetDetailsPropTypes } from '../../propTypes/petDetails';
import { SuccessMessageDialog } from '../successMessage/successMessageDialog';
import { FORM_KEYS } from '../../constants/formKeys';
import ErrorText from '../shared/ErrorText/ErrorText';
import Loading from '../shared/Loading/Loading';
import { EmptyRecord } from '../emptyRecord/emptyRecord';
import { EMPTY_RECORD_MESSAGE } from '../../constants/constants';
import { setVetDetailsList, clearNewlyAddedVets } from '../../store/slice/vetDetailsSlice';
import { SLICES } from '../../constants/slices';
import { useGetPolicyVetsPerClaimQuery, useGetVetsQuery } from '../../data/vets';
import { CSPUrlsMap } from '../../routes/CSPUrlsMap';

export const VetDetails = ({
  isReadOnly = false,
  isProfileView = false,
  petDetails,
  activeVetNumber,
  activePolicyNumber
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { auth0AccessToken } = authDetails;
  const customerClaimId = useSelector((state) => state?.[SLICES.CLAIMS]?.customerClaimId);
  const { data: policyVetDetails, isLoading: isPolicyVetDetailsLoading } = useGetPolicyVetsPerClaimQuery(
    { customerClaimId: customerClaimId || 0, auth0Token: auth0AccessToken },
    { skip: isProfileView || !auth0AccessToken }
  );
  const { data: allVetDetails, isLoading: isAllVetDetailsLoading } = useGetVetsQuery(
    { auth0Token: auth0AccessToken },
    {
      skip: !isProfileView || !auth0AccessToken
    }
  );
  const vetDetails = isProfileView ? allVetDetails : policyVetDetails;
  const vetDetailsLoading = isProfileView ? isAllVetDetailsLoading : isPolicyVetDetailsLoading;
  const selectedVetDetails = useSelector((state) => state?.[SLICES.VET_DETAILS].newlyAddedVets);
  const vetDetailsList = useSelector((state) => state?.[SLICES.VET_DETAILS].vetDetailsList);
  const fieldFormContext = useFormContext();
  const [isVetPopupActive, setIsVetPopupActive] = useState(false);
  const [activePet, setActivePet] = useState();
  const [activeVet, setActiveVet] = useState();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [policyFilter, setPolicyFilter] = useState();
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  useEffect(() => {
    // Initialize vetDetailsList with vetDetails
    dispatch(setVetDetailsList(vetDetails));
    dispatch(clearNewlyAddedVets());
  }, [dispatch, vetDetails]);

  useEffect(() => {
    if (location.pathname === CSPUrlsMap.vetDetails.url) {
      setPolicyFilter('');
    }
  }, [location.pathname]);

  const petOptions = [{ label: 'All pets', value: '' }, ...GetPetPolicyNumberDropdownOptions({ petDetails })];
  useEffect(() => {
    if (activePolicyNumber) {
      const activePetMatch = GetPetDetailsFromPolicyNumber({ petDetails, policyNumber: activePolicyNumber });
      setActivePet(activePetMatch);
      setShowErrorMessage(false);
    }
    if (activeVetNumber) {
      const activeVetMatch = GetVetsByServiceProviderNumber({ vetDetails, providerNumber: activeVetNumber });
      setActiveVet(activeVetMatch);
    }
  }, [activeVetNumber, activePolicyNumber, petDetails, vetDetails]);

  useEffect(() => {
    if (activePolicyNumber) {
      setPolicyFilter(activePolicyNumber);
    }
  }, [activePolicyNumber]);

  useEffect(() => {
    if (selectedVetDetails.length > 0) {
      const transformedData = selectedVetDetails?.map((selectedVet) => ({
        VetId: 0,
        ServiceProviderNumber: selectedVet.activeVet.serviceProviderNumber,
        ServiceProviderName: selectedVet.activeVet.serviceProvider,
        ServiceProviderAddress: selectedVet.activeVet.AddressLine1,
        Suburb: selectedVet.activeVet.suburb,
        State: selectedVet.activeVet.state,
        Postcode: selectedVet.activeVet.postCode,
        ServiceProviderContactNumber: selectedVet.activeVet.phoneNumber,
        EmailAddress: selectedVet.activeVet.email || null,
        VetName: '',
        PetsTreated: selectedVet.petsTreated
      }));

      const updatedVetDetailsList = [
        ...vetDetailsList.map((vet) => {
          const replaceCurrentVetDetails = transformedData.find(
            (t) => t.ServiceProviderNumber === vet.ServiceProviderNumber
          );
          return replaceCurrentVetDetails || vet;
        }),
        ...transformedData.filter(
          (t) => !vetDetailsList.some((v) => v.ServiceProviderNumber === t.ServiceProviderNumber)
        )
      ];

      dispatch(setVetDetailsList(updatedVetDetailsList));
    } else {
      dispatch(setVetDetailsList(vetDetails));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVetDetails, vetDetails, activePet, dispatch]);

  const handleVetUpdateTrigger = ({ vetDetail }) => {
    setActiveVet(vetDetail);
    setIsVetPopupActive(true);
  };

  const handleVetAddTrigger = async () => {
    const isValid = await fieldFormContext.trigger([FORM_KEYS.PET_DETAILS_RADIOS], {
      shouldFocus: true
    });
    if (isValid) {
      setShowErrorMessage(false);
      setActiveVet(null);
      setIsVetPopupActive(true);
    } else {
      setShowErrorMessage(true);
    }
  };

  const handlePetFilter = (event) => {
    setPolicyFilter(event.target.value);
  };

  const PetVetDetailsHeading = () => {
    if (isProfileView) {
      return null;
    }

    return (
      <>
        <h3>
          Are all of {activePet && activePet.Name ? `${activePet.Name}` : 'your pet'}&apos;s vets listed below? Add or
          update details of your vets below &#40;optional&#41;
        </h3>
        <p>
          If we need further supporting documents, we may contact your vet&#40;s&#41; on your behalf to complete your
          claim.
        </p>
      </>
    );
  };

  return (
    <div className="petVetDetails">
      <PetVetDetailsHeading />

      {!isProfileView && !isReadOnly && !vetDetailsLoading && (
        <div className="claimAddAVetButtonContainer">
          <button
            type="button"
            className="button primary"
            onClick={handleVetAddTrigger}
          >
            <span className="labelText">Add a vet</span>
          </button>
        </div>
      )}
      {isProfileView && !vetDetailsLoading && (
        <div className="vetDetailsHeaderSection">
          <div className="filterContainer">
            <h5 aria-hidden="true">Filter by</h5>
            <Dropdown
              fieldFormContext={fieldFormContext}
              name="petFilter"
              value={policyFilter}
              options={petOptions}
              label="Filter claims by pet"
              hideLabel
              customChangeHandler={handlePetFilter}
              includeEmptyOption={false}
            />
          </div>
          {!isReadOnly && (
            <button
              type="button"
              className="button primary"
              onClick={handleVetAddTrigger}
            >
              <span className="labelText">Add a vet</span>
            </button>
          )}
        </div>
      )}

      <ErrorText
        cssClass="custom_validation"
        message={[{ value: 'Please select a pet to add a vet', isDisplayed: showErrorMessage }]}
      />

      {vetDetailsLoading && <Loading />}

      {!vetDetailsLoading && vetDetailsList?.length > 0 && (
        <VetDetailsList
          isEditable={!isReadOnly}
          isFullDetailView={isProfileView}
          petDetails={petDetails}
          vetDetails={vetDetailsList}
          activePolicyNumber={activePolicyNumber}
          onVetUpdate={handleVetUpdateTrigger}
          filter={policyFilter}
        />
      )}

      {!vetDetailsLoading && vetDetailsList?.length === 0 && isProfileView && (
        <EmptyRecord message={EMPTY_RECORD_MESSAGE.VET_DETAILS_LIST} />
      )}

      {!isReadOnly && isProfileView && !vetDetailsLoading && vetDetailsList?.length > 0 && (
        <div className="addAVetButtonContainer">
          <button
            type="button"
            className="button primary"
            onClick={handleVetAddTrigger}
          >
            <span className="labelText">Add a vet</span>
          </button>
        </div>
      )}

      {!isReadOnly && isVetPopupActive && (
        <VetDetailPopup
          petDetails={petDetails}
          vetDetail={activeVet}
          onCancel={() => setIsVetPopupActive(false)}
          setShowSuccessMessage={setShowSuccessMessage}
          activePet={activePet}
          isClaimVetDetails={!isProfileView}
        />
      )}
      {showSuccessMessage && (
        <SuccessMessageDialog
          message={`Thanks, your vet details will be ${
            activeVet ? 'updated' : 'added'
          }. Please refresh your page in a moment to view the changes`}
          onClose={() => setShowSuccessMessage(false)}
        />
      )}
    </div>
  );
};

VetDetails.propTypes = {
  isReadOnly: PropTypes.bool,
  isProfileView: PropTypes.bool,
  petDetails: PropTypes.arrayOf(FullPetDetailsPropTypes),
  activeVetNumber: PropTypes.string,
  activePolicyNumber: PropTypes.string
};
